import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { driver, type Config, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuthStore } from '@/stores/auth.store';
import { isLyrraOne } from '@/lib/platform';
import { isTrainerDashboardTourEligibleRole } from './trainerTourEligibility';
import {
  loadTrainerTourState,
  markTrainerTourDismissed,
  markTrainerTourDashboardDone,
  markTrainerTourRouteShown,
} from './trainerTourStorage';
import { isLyrraOneSetupWizardCompleted, isLyrraOneSetupWizardOpen } from '@/lib/lyrraone-setup-wizard';

function resolveRouteHintKey(pathname: string, search: string): string | null {
  if (pathname === '/learners') return isLyrraOne() ? null : 'learners';
  if (pathname === '/analytics') return 'analytics';
  if (pathname === '/dashboard') {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'flows' && isLyrraOne()) return 'eduflow';
    if (tab === 'supports' && isLyrraOne()) return 'podcasts';
  }
  if (pathname === '/eduflow/list' || pathname === '/eduflow') return isLyrraOne() ? null : 'eduflow';
  if (pathname === '/podcasts' || pathname.startsWith('/podcasts/')) return isLyrraOne() ? null : 'podcasts';
  if (pathname === '/videos') return 'videos';
  if (pathname === '/sounds') return 'sounds';
  if (pathname === '/images') return 'images';
  if (pathname === '/documents') return 'documents';
  if (pathname === '/help') return 'help';
  if (pathname === '/producer-assignments') return 'producerAssignments';
  if (pathname === '/webinars') return 'webinars';
  return null;
}

const ROUTE_SELECTORS: Record<string, string> = {
  learners: '[data-tour="tour-learners-root"]',
  analytics: '[data-tour="tour-analytics-root"]',
  eduflow: '[data-tour="tour-eduflow-list-root"]',
  podcasts: '[data-tour="tour-podcasts-root"]',
  videos: '[data-tour="tour-videos-root"]',
  sounds: '[data-tour="tour-sounds-root"]',
  images: '[data-tour="tour-images-root"]',
  documents: '[data-tour="tour-documents-root"]',
  help: '[data-tour="tour-help-root"]',
  producerAssignments: '[data-tour="tour-producer-assignments-root"]',
  webinars: '[data-tour="tour-webinars-root"]',
};

function waitForSelector(selector: string, timeoutMs: number, signal: { cancelled: boolean }): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(true);
      return;
    }
    const t0 = Date.now();
    const id = window.setInterval(() => {
      if (signal.cancelled) {
        window.clearInterval(id);
        resolve(false);
        return;
      }
      if (document.querySelector(selector)) {
        window.clearInterval(id);
        resolve(true);
        return;
      }
      if (Date.now() - t0 > timeoutMs) {
        window.clearInterval(id);
        resolve(false);
      }
    }, 100);
  });
}

/**
 * Visite guidée formateur (première connexion) : tableau de bord puis infobulle à l’ouverture de chaque page du menu.
 */
export function TrainerTourRunner() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const role = user?.role;
  const eligible = isTrainerDashboardTourEligibleRole(role);

  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const dashboardCloseIsAbortRef = useRef(false);

  useEffect(() => {
    if (!eligible || !userId) return undefined;

    if (isLyrraOne() && (!isLyrraOneSetupWizardCompleted(user?.preferences) || isLyrraOneSetupWizardOpen())) {
      return undefined;
    }

    const pathnameAtEffect = location.pathname;
    const searchAtEffect = location.search;

    const destroyDriver = () => {
      try {
        driverRef.current?.destroy();
      } catch {
        // noop
      }
      driverRef.current = null;
    };

    const signal = { cancelled: false };

    const baseConfig: Partial<Config> = {
      showProgress: true,
      progressText: t('trainerTour.driver.progress'),
      nextBtnText: t('trainerTour.driver.next'),
      prevBtnText: t('trainerTour.driver.prev'),
      doneBtnText: t('trainerTour.driver.done'),
      allowClose: true,
      overlayOpacity: 0.72,
      smoothScroll: true,
      popoverClass: 'lyrra-trainer-tour-popover',
    };

    const timer = window.setTimeout(() => {
      void (async () => {
        const st = loadTrainerTourState(userId);
        if (signal.cancelled || st.dismissed) return;

        destroyDriver();

        if (pathnameAtEffect === '/dashboard' && !st.dashboardWalkthroughDone) {
          if (window.location.pathname !== '/dashboard') return;
          dashboardCloseIsAbortRef.current = false;
          const steps: DriveStep[] = [
            {
              popover: {
                title: t('trainerTour.welcome.title'),
                description: t('trainerTour.welcome.description'),
                side: 'over',
              },
            },
            {
              element: '[data-tour="tour-dash-header"]',
              popover: {
                title: t('trainerTour.dashboard.header.title'),
                description: t('trainerTour.dashboard.header.description'),
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="tour-dash-actions"]',
              popover: {
                title: t('trainerTour.dashboard.actions.title'),
                description: t('trainerTour.dashboard.actions.description'),
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="tour-dash-stats"]',
              popover: {
                title: t('trainerTour.dashboard.stats.title'),
                description: t('trainerTour.dashboard.stats.description'),
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="tour-dash-activity"]',
              popover: {
                title: t('trainerTour.dashboard.activity.title'),
                description: t('trainerTour.dashboard.activity.description'),
                side: 'top',
              },
            },
            {
              element: '[data-tour="tour-sidebar-nav"]',
              popover: {
                title: t('trainerTour.sidebar.title'),
                description: t('trainerTour.sidebar.description'),
                side: 'right',
              },
            },
          ];

          const d = driver({
            ...baseConfig,
            steps,
            onCloseClick: () => {
              dashboardCloseIsAbortRef.current = true;
              d.destroy();
            },
            onDestroyed: () => {
              driverRef.current = null;
              if (dashboardCloseIsAbortRef.current) {
                markTrainerTourDismissed(userId);
              } else {
                markTrainerTourDashboardDone(userId);
              }
              dashboardCloseIsAbortRef.current = false;
            },
          } as Config);
          driverRef.current = d;
          d.drive();
          return;
        }

        const fresh = loadTrainerTourState(userId);
        if (!fresh.dashboardWalkthroughDone || fresh.dismissed || signal.cancelled) return;

        const routeKey = resolveRouteHintKey(pathnameAtEffect, searchAtEffect);
        if (!routeKey || fresh.routeHintsShown.includes(routeKey)) return;

        const selector = ROUTE_SELECTORS[routeKey];
        if (!selector) return;

        const found = await waitForSelector(selector, 5000, signal);
        if (!found || signal.cancelled) return;
        if (`${window.location.pathname}${window.location.search}` !== `${pathnameAtEffect}${searchAtEffect}`) return;

        const d = driver({
          ...baseConfig,
          showProgress: false,
          steps: [
            {
              element: selector,
              popover: {
                title: t(`trainerTour.routes.${routeKey}.title`),
                description: t(`trainerTour.routes.${routeKey}.description`),
                side: 'bottom',
                showButtons: ['next', 'close'],
                nextBtnText: t('trainerTour.driver.gotIt'),
              },
            },
          ],
          onCloseClick: (_el, _step, { driver: drv }) => {
            drv.destroy();
          },
          onDestroyed: () => {
            driverRef.current = null;
            markTrainerTourRouteShown(userId, routeKey);
          },
        } as Config);
        driverRef.current = d;
        d.drive();
      })();
    }, 520);

    return () => {
      signal.cancelled = true;
      window.clearTimeout(timer);
      destroyDriver();
    };
  }, [eligible, userId, user?.preferences, location.pathname, location.search, t, i18n.language, i18n.resolvedLanguage]);

  return null;
}
